import React, { useEffect, useState } from 'react';
import { Assignment, AccountBalance, CheckCircle, Schedule } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/apiService';

const AgentDashboard = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAgentSummary();
  }, []);

  const fetchAgentSummary = async () => {
    try {
      const response = await apiService.get(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/agent-dashboard-summary`
      );

      if (response.ok) {
        const data = await response.json();
        setSummary(data.summary);
      } else {
        console.error('Failed to fetch agent summary');
      }
    } catch (error) {
      console.error('Error fetching agent summary:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-10">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-800">
          Welcome, <span className="text-blue-600">{user?.firstName}</span>
        </h1>
        <p className="text-gray-600 mt-2">Here's your personal collections performance summary</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Loans Assigned */}
        <div className="bg-white border border-blue-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 uppercase font-semibold">Loans Assigned</p>
              <h3 className="text-3xl font-bold text-blue-600 mt-2">
                {summary?.totalLoansAssigned || 0}
              </h3>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <Assignment className="text-blue-600" style={{ fontSize: 32 }} />
            </div>
          </div>
        </div>

        {/* Total Amount Assigned */}
        <div className="bg-white border border-cyan-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 uppercase font-semibold">Amount Assigned</p>
              <h3 className="text-3xl font-bold text-cyan-600 mt-2">
                ${summary?.totalAmountAssigned ? (summary.totalAmountAssigned / 1000).toFixed(1) : 0}K
              </h3>
            </div>
            <div className="bg-cyan-100 p-3 rounded-full">
              <AccountBalance className="text-cyan-600" style={{ fontSize: 32 }} />
            </div>
          </div>
        </div>

        {/* Total Collected */}
        <div className="bg-white border border-green-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 uppercase font-semibold">Collected</p>
              <h3 className="text-3xl font-bold text-green-600 mt-2">
                ${summary?.totalCollected ? (summary.totalCollected / 1000).toFixed(1) : 0}K
              </h3>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <CheckCircle className="text-green-600" style={{ fontSize: 32 }} />
            </div>
          </div>
          <div className="mt-3 text-xs text-gray-500">
            Collection Rate: <span className="font-semibold text-green-600">{summary?.collectionRate || 0}%</span>
          </div>
        </div>

        {/* Total Promised */}
        <div className="bg-white border border-orange-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 uppercase font-semibold">Promised</p>
              <h3 className="text-3xl font-bold text-orange-600 mt-2">
                ${summary?.totalPromised ? (summary.totalPromised / 1000).toFixed(1) : 0}K
              </h3>
            </div>
            <div className="bg-orange-100 p-3 rounded-full">
              <Schedule className="text-orange-600" style={{ fontSize: 32 }} />
            </div>
          </div>
        </div>
      </div>

      {/* Performance Overview */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Performance Overview</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Outstanding Balance */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <p className="text-sm text-gray-600 uppercase font-semibold mb-2">Outstanding Balance</p>
            <h3 className="text-4xl font-bold text-gray-800">
              ${summary?.outstandingBalance ? (summary.outstandingBalance / 1000).toFixed(1) : 0}K
            </h3>
            <p className="text-xs text-gray-500 mt-2">
              Amount still to be collected from assigned loans
            </p>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <p className="text-sm text-gray-600 uppercase font-semibold mb-4">Quick Stats</p>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Avg. per Loan:</span>
                <span className="font-bold text-gray-800">
                  ${summary?.totalLoansAssigned > 0
                    ? ((summary.totalAmountAssigned / summary.totalLoansAssigned) / 1000).toFixed(2)
                    : 0}K
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Collection Rate:</span>
                <span className="font-bold text-green-600">{summary?.collectionRate || 0}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Promised Amount:</span>
                <span className="font-bold text-orange-600">
                  ${summary?.totalPromised ? (summary.totalPromised / 1000).toFixed(1) : 0}K
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Need Help?</h3>
        <p className="text-sm text-gray-600">
          View your assigned loans in the <strong>Assigned Loans</strong> section from the sidebar,
          or track your promises in the <strong>Promises</strong> section.
        </p>
      </div>
    </div>
  );
};

export default AgentDashboard;
